import React, { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../Service/authConfig.js";
import { InteractionStatus } from "@azure/msal-browser";
import ERPReleasePopup from "../component/ERPReleasePopup.js";

const modules = [
  {
    href: "https://shaml-HC.awjholding.com",
    icon: "images/hr.svg",
    title: "SHAML Human Capital",
    description: "HR, payroll & employee services",
  },
  {
    href: "https://shamlcore.awjholding.com/sap/bc/ui2/flp",
    icon: "images/procurement.svg",
    title: "SHAML Procurement",
    description: "Sourcing, contracts & purchasing",
  },
  {
    href: "https://shamlcore.awjholding.com/sap/bc/ui2/flp",
    icon: "images/finance.svg",
    title: "SHAML Finance & Control",
    description: "Accounting, treasury & reporting",
  },
  {
    href: "https://shamlcore.awjholding.com/sap/bc/ui2/flp",
    icon: "images/project.svg",
    title: "SHAML Portfolio & Project Managment",
    description: "Projects, portfolios & delivery",
  },
];

function HomePage() {
  const { accounts, instance, inProgress } = useMsal();
  const [showERPPopup, setShowERPPopup] = useState(false);
  const ssoFallbackTriggered = useRef(false);

  const user = accounts.length > 0 ? accounts[0] : null;

  const shouldShowPopup = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date('2026-01-19');
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 3);
    return today <= endDate;
  };

  useEffect(() => {
    if (!shouldShowPopup()) return;
    setShowERPPopup(true);
  }, []);

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;
    if (instance.getAllAccounts()[0]) return;
    if (ssoFallbackTriggered.current) return;
    ssoFallbackTriggered.current = true;

    const loginHint = new URLSearchParams(window.location.search).get("login_hint");
    instance.loginRedirect({
      ...loginRequest,
      ...(loginHint ? { loginHint } : {}),
    });
  }, [inProgress, instance]);

  if (!user) {
    return (
      <div className="min-h-screen w-full bg-gradient-to-br from-primary-50 via-white to-primary-50 flex justify-center items-center">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mb-4"></div>
          <p className="text-primary-700 font-medium">Signing you in…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-primary-50 via-white to-primary-50">
      {/* Decorative blurred background blobs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-primary-200/40 blur-3xl"></div>
        <div className="absolute top-1/2 -right-32 h-[28rem] w-[28rem] rounded-full bg-primary-100/50 blur-3xl"></div>
      </div>

      <main className="relative max-w-portal mx-auto px-6 py-16 md:py-24">
        {/* Logo */}
        <div className="flex justify-center">
          <img
            src="images/shaml-logo.svg"
            alt="Shaml"
            className="h-16 md:h-20 w-auto drop-shadow-card"
          />
        </div>

        {/* Heading */}
        <h1 className="mt-8 text-center text-3xl md:text-5xl font-bold text-primary-900 tracking-tight">
          Welcome to <span className="text-primary-500">Shaml Platform</span>
        </h1>
        <p className="mt-4 mx-auto max-w-2xl text-center text-base md:text-lg text-slate-600 leading-relaxed">
          A unified system that integrates
          <span className="text-primary-700 font-semibold"> HR, Finance, Procurement, and Project System </span>
          management to support and grow the AWJ business.
        </p>

        {/* Module cards */}
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {modules.map((m) => (
            <a
              key={m.title}
              href={m.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative bg-white rounded-2xl p-6 shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1 border border-primary-100/60"
            >
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary-50 group-hover:bg-primary-100 transition-colors">
                <img src={m.icon} alt="" className="h-8 w-8" />
              </div>
              <h3 className="mt-5 text-lg font-semibold text-primary-800 leading-snug">
                {m.title}
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                {m.description}
              </p>
              <div className="mt-5 flex items-center gap-2 text-sm font-medium text-primary-600 group-hover:text-primary-700">
                <span>Open</span>
                <svg
                  className="h-4 w-4 transition-transform group-hover:translate-x-1"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14M13 5l7 7-7 7" />
                </svg>
              </div>
            </a>
          ))}
        </div>

      </main>

      {showERPPopup && (
        <ERPReleasePopup
          isOpen={showERPPopup}
          onClose={() => setShowERPPopup(false)}
        />
      )}
    </div>
  );
}

export default HomePage;
